import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { getApiUrl } from '../helpers/rest-utils';
import { HttpClient } from '@angular/common/http';
import { concatMap, filter, finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { User, UserService } from '../user/user.service';
import { BillingProvider, Feature, InvoicePreview, Subscription, SubscriptionPlan, SubscriptionPlanName, SubscriptionPrice, SubscriptionStatus, SubscriptionType, SubscriptionInterval, SubscriptionDetails, ProviderPlan } from './subscription';
import { AgentService } from '../agent/agent.service';
import { formatSecondsAsMinutes } from '../helpers/utils';
import { RosieCurrencyPipe } from '../../shared/pipes/rosie-currency.pipe';
import { FormControl } from '@angular/forms';
import { AnalyticsEvent, InternalAnalyticsService } from '../internal-analytics.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountSection } from '../../account/account.service';
import { SessionService } from '../session.service';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  planOptions: any = [];
  selectedPlanControl = new FormControl();
  selectedPlanChanged: Subject<SubscriptionPlanName> = new Subject();
  selectedInterval: SubscriptionInterval;
  isPaymentMethodInvalid: boolean = false;
  paymentMethodErrors = [];
  open = new Subject<{ feature?: Feature, plan?: SubscriptionPlanName, origin?: SubscriptionLayout, websiteTextingPreSelected?: boolean } | null>();
  close: Subject<boolean> = new Subject();
  initialize: Subject<boolean> = new Subject();
  reset: Subject<boolean> = new Subject();
  created: Subject<boolean> = new Subject();
  websiteTextingPreSelected$ = new BehaviorSubject<boolean>(false);
  credentials: { publishableKey?: string, clientSecret?: string } = {};
  plans: any;
  invoicePreview: InvoicePreview;
  isLoadingInvoicePreview: boolean = false;
  upgradeToPlanActive: SubscriptionPlanName;
  isExpanded: boolean = true;
  isPlanChangeInProgress: SubscriptionPlanName | boolean = false;
  hasPaymentFailedTriggered = false;
  layout: SubscriptionLayout;
  subscriptionDetails: SubscriptionDetails | null = null;
  appliedCoupon: any = null;
  couponChanged: Subject<string> = new Subject();
  selectedPlanDisplay: any = null;
  isRevalidatingCoupon: boolean = false;
  constructor(private http: HttpClient, private user: UserService, private agent: AgentService, private currency: RosieCurrencyPipe, private analytics: InternalAnalyticsService, private router: Router, private session: SessionService) {
    // Initialize selectedInterval based on user's current interval
    this.selectedInterval = this.initialInterval;

    this.selectedPlanChanged.pipe(filter(() => !!this.upgradeToPlanActive)).subscribe(() => {
      if (this.upgradeToPlanActive) {
        const plan = this.planOptions.find((plan) => plan.value === this.upgradeToPlanActive);
        if (plan) {
          this.isExpanded = true;
          this.selectPlan(plan, true);
        }
      }
    });
    this.session.refreshState.subscribe(() => this.getBilling().subscribe());

    this.open.subscribe((data) => {
      if (data?.websiteTextingPreSelected !== undefined && data?.websiteTextingPreSelected !== null) {
        this.websiteTextingPreSelected$.next(data.websiteTextingPreSelected);
      } else if (!this.websiteTextingPreSelected$.value) {
        const agentValue = this.agent.primary?.configuration?.websiteTextingPreSelected || false;
        this.websiteTextingPreSelected$.next(agentValue);
      }
    });
  }

  get userData(): User {
    return this.user.userData;
  }

  get subscriptionData(): Subscription {
    return this.userData?.subscription;
  }

  get minutesRemainingInTrialFormatted(): string {
    return formatSecondsAsMinutes(this.agent.primary?.trialSecondsRemaining);
  }

  get hasCreditCardOnFile(): boolean {
    return this.user.hasCreditCardOnFile;
  }

  get isCanceledAtEndOfBillingCycle(): boolean {
    return this.subscriptionData?.isCanceled && !!this.subscriptionData?.billingDate && !this.isCanceled;
  }

  get hasMinutesRemainingInTrial(): boolean {
    return !!this.agent.primary?.trialSecondsRemaining;
  }

  get hasOnlyIntervalChange(): boolean {
    return this.initialPlan === this.selectedPlan?.name && this.initialInterval !== this.selectedInterval;
  }

  get hasAnyChange(): boolean {
    return this.initialPlan !== this.selectedPlan?.name || this.initialInterval !== this.selectedInterval;
  }

  get isTrialing(): boolean {
    return this.subscriptionData?.status === SubscriptionStatus.Trialing;
  }

  get isActive(): boolean {
    return this.subscriptionData?.status === SubscriptionStatus.Active;
  }

  get isCanceled(): boolean {
    return this.subscriptionData?.status === SubscriptionStatus.Canceled;
  }

  get formattedPlanCost(): string {
    return this.currency.transform(this.subscriptionData?.planCost);
  }

  get isPaused(): boolean {
    return this.subscriptionData?.status === SubscriptionStatus.Paused;
  }

  get isExpired(): boolean {
    return this.subscriptionData?.status === SubscriptionStatus.Expired;
  }

  get isPastDue(): boolean {
    return this.subscriptionData?.status === SubscriptionStatus.PastDue;
  }

  get hasCreditCardAdded(): boolean {
    return this.subscriptionData?.isCreditCardAdded;
  }

  get billingUrl(): string {
    return this.subscriptionData?.billingUrl;
  }

  get billingProvider(): BillingProvider {
    return this.subscriptionData?.billingProvider
      ?? (this.user.activeBusiness?.billingProvider as BillingProvider)
      ?? BillingProvider.Stripe;
  }

  get isExternalBillingProvider(): boolean {
    const provider = this.billingProvider;
    return !!provider && provider !== BillingProvider.Stripe;
  }

  get isAppleBillingProvider(): boolean {
    return this.billingProvider === BillingProvider.Apple;
  }

  get isShopifyBillingProvider(): boolean {
    return this.billingProvider === BillingProvider.Shopify;
  }

  get isPendingApproval(): boolean {
    return this.subscriptionData?.status === SubscriptionStatus.PendingApproval;
  }

  /**
   * Whether the billing provider reports the subscription as live (ACTIVE).
   * For Shopify, provider_status is ACTIVE for the whole life of the sub (trial
   * and paid). This is the authoritative "there is a live charge" signal and is
   * used to defend against a stale local pending_approval status routing the user
   * into the retry/replace flow. Compared case-insensitively (we store 'active',
   * Shopify's API returns 'ACTIVE').
   */
  get isProviderActive(): boolean {
    return this.subscriptionData?.providerStatus?.toLowerCase() === 'active';
  }

  get providerConfirmationUrl(): string {
    return this.subscriptionData?.providerConfirmationUrl;
  }

  get selectedPlan(): SubscriptionPlan {
    const selectedPlanName = this.selectedPlanControl.value?.value;
    return this.plans?.find(plan => plan.name === selectedPlanName);
  }

  get selectedPlanPrice(): number {
    if (this.selectedInterval === SubscriptionInterval.Yearly) {
      const monthlyPrice = this.selectedPlan?.prices?.find(price =>
        price.usageType === SubscriptionType.Licensed &&
        price.interval === SubscriptionInterval.Monthly
      );

      if (monthlyPrice) {
        return monthlyPrice.amount * 10;
      }
    }

    const price = this.selectedPlan?.prices?.find(price =>
      price.usageType === SubscriptionType.Licensed &&
      price.interval === (this.selectedInterval || SubscriptionInterval.Monthly)
    );

    return price?.amount || 0;
  }

  get initialPlan() {
    const userPlan = this.userData?.subscription?.planName;
    return (userPlan && userPlan !== SubscriptionPlanName.Trial && userPlan !== SubscriptionPlanName.None) || this.isEnterprisePlanSelected ? userPlan : SubscriptionPlanName.Scale;
  }

  get initialInterval(): SubscriptionInterval {
    const currentCost = this.userData?.subscription?.planCost;
    const currentPlanName = this.initialPlan;
    const planData = this.plans?.find(plan => plan.name === currentPlanName);
    const matchingPrice = planData?.prices.find(price => price.amount === currentCost);
    return matchingPrice?.interval === SubscriptionInterval.Yearly ? SubscriptionInterval.Yearly : SubscriptionInterval.Monthly;
  }

  get isDowngrade(): boolean {
    const initialPlan = this.initialPlan;
    const initialInterval = this.initialInterval;
    const selectedInterval = this.selectedInterval;
    const isDowngradeFromScalePlan = initialPlan === SubscriptionPlanName.Scale && this.selectedPlan?.name === SubscriptionPlanName.Professional;
    const isDowngradeFromGrowthPlan = initialPlan === SubscriptionPlanName.Growth && (this.selectedPlan?.name === SubscriptionPlanName.Professional || this.selectedPlan?.name === SubscriptionPlanName.Scale);
    return isDowngradeFromScalePlan || isDowngradeFromGrowthPlan || (initialInterval === SubscriptionInterval.Yearly && selectedInterval === SubscriptionInterval.Monthly);
  }

  get isUpgrade(): boolean {
    const initialPlan = this.initialPlan;
    const selectedPlan = this.selectedPlan?.name;
    const initialInterval = this.initialInterval;
    const selectedInterval = this.selectedInterval;

    const isUpgradeFromProfessionalPlan = initialPlan === SubscriptionPlanName.Professional && (selectedPlan === SubscriptionPlanName.Scale || selectedPlan === SubscriptionPlanName.Growth);
    const isUpgradeFromScalePlan = initialPlan === SubscriptionPlanName.Scale && (selectedPlan === SubscriptionPlanName.Growth);

    return isUpgradeFromProfessionalPlan || isUpgradeFromScalePlan || (initialInterval === SubscriptionInterval.Monthly && selectedInterval === SubscriptionInterval.Yearly);
  }

  get isEnterprisePlanSelected(): boolean {
    if (this.isPaused) {
      return false;
    }
    const userPlan = this.userData?.subscription?.planName;
    const availablePlans = Object.values(SubscriptionPlanName)
    return !availablePlans.some(plan => plan === userPlan);
  }

  get isLoadingInvoice(): boolean {
    return this.isLoadingInvoicePreview;
  }

  get isUpgradeToPlanActive(): boolean {
    return !!this.upgradeToPlanActive;
  }

  getBilling(): Observable<{ publishableKey: string, secret: string }> {
    return this.http.get(`${getApiUrl()}billing`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res?.data), tap((credentials) => this.credentials = credentials));
  }

  // One in-flight plans request is shared by every caller: the checkout is
  // mounted in several places and each business switch asks all of them to
  // refresh, which used to fan out into parallel Stripe reads.
  private plansRequest$: Observable<any> | null = null;

  getPlans(): Observable<any> {
    if (!this.plansRequest$) {
      this.plansRequest$ = this.http.get(`${getApiUrl()}subscription-plans`)
        .pipe(
          map((res: any) => res?.data),
          // Runs once per HTTP response (before the share), not once per subscriber.
          tap((plans) => {
            this.plans = plans;
            const planWithCoupon = plans?.find(p => p.couponPreview);
            if (planWithCoupon?.couponPreview?.coupon) {
              this.appliedCoupon = planWithCoupon.couponPreview.coupon;
              this.couponChanged.next(planWithCoupon.couponPreview.coupon.id);
            } else {
              this.appliedCoupon = null;
              this.couponChanged.next('');
            }
          }),
          finalize(() => this.plansRequest$ = null),
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }
    return this.plansRequest$;
  }

  handleSubscription(prices: SubscriptionPrice[], createNewSubscription = false, paymentMethod?: string, redirectToBillingOnCreate = true): Observable<any> {
    if (this.isExternalBillingProvider) {
      return new Observable(subscriber => subscriber.complete());
    }

    const licensedPrice = prices.find(price => price.usageType === SubscriptionType.Licensed);

    if (!licensedPrice) {
      // Fail BEFORE touching the subscription endpoints: the card was already
      // attached by the caller, and crashing here used to leave the flow half
      // done (card on file, no subscription, TypeError in the console).
      return new Observable(subscriber => subscriber.error(new Error('No licensed price available for the selected plan.')));
    }

    const data: any = {};
    if (paymentMethod) { data.paymentMethod = paymentMethod; }
    if (this.appliedCoupon?.id) { data.couponCode = this.appliedCoupon.promotionCodeId || this.appliedCoupon.code || this.appliedCoupon.id; }
    data.prices = [{ priceId: licensedPrice.id, type: SubscriptionType.Licensed }];

    // A null userData.subscription is usually STALE client state (the trial
    // subscription exists server-side but loaded after this session's user
    // fetch) - it used to crash here with PUT subscriptions/null AFTER the card
    // was already attached. Refresh the user before deciding create vs update,
    // so a stale null can never crash the flow or double-create a subscription;
    // only a genuinely missing row creates a new one.
    const ensureFreshSubscription$ = this.user.subscription ? of(null) : this.user.getUser();
    // Resolved inside the switchMap (post-refresh); read again by the final tap
    // to decide the billing-page redirect.
    let shouldCreateNewSubscription = false;

    return ensureFreshSubscription$.pipe(switchMap(() => {
      shouldCreateNewSubscription = this.isCanceled || !this.user.subscription;

      return this.http[shouldCreateNewSubscription ? 'post' : 'put'](`${getApiUrl()}subscriptions/${shouldCreateNewSubscription ? '' : this.user.subscription.id}`, data, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } });
    }))
      .pipe(map((res: any) => res?.data), tap((subscription: Subscription) => {
        const user = this.user.userData;
        user.subscription = subscription;
        this.user.refreshUser(user);
        this.invoicePreview = null;
      }), concatMap(() => this.agent.getAgent()),
        tap(() => {
          this.isPlanChangeInProgress = false
          // Emit created subject to notify subscribers of subscription changes
          this.created.next(true);
          if (shouldCreateNewSubscription && redirectToBillingOnCreate) {
            this.router.navigate(['/account', { section: AccountSection.Billing }]);
          }
        }));
  }

  getInvoicePreview(prices: SubscriptionPrice[]): Observable<InvoicePreview> {
    this.isLoadingInvoicePreview = true;
    const licensedPrice = prices.find(price => price.usageType === SubscriptionType.Licensed);
    const data: any = { prices: [{ priceId: licensedPrice.id, type: SubscriptionType.Licensed }] };
    if (this.appliedCoupon?.id) { data.couponCode = this.appliedCoupon.promotionCodeId || this.appliedCoupon.code || this.appliedCoupon.id; }

    return this.http.post(`${getApiUrl()}subscriptions/invoice/preview`, data)
      .pipe(map((res: any) => res?.data), tap((invoicePreview: InvoicePreview) => { this.invoicePreview = invoicePreview; this.isLoadingInvoicePreview = false }));
  }

  selectPlan(plan: any, disablePreselect?: boolean, isManualSelection?: boolean) {
    this.selectedPlanControl.patchValue(plan);

    if (!disablePreselect || this.hasCreditCardOnFile) {
      this.isExpanded = false;
    }

    if (this.appliedCoupon && this.subscriptionData?.id && !this.isRevalidatingCoupon && isManualSelection) {
      this.revalidateCouponForSelectedPlan();
    }

    if (this.isTrialing && !this.hasCreditCardOnFile && !disablePreselect) {
      this.isPlanChangeInProgress = this.selectedPlan.name;
      this.analytics.trackEvent(AnalyticsEvent.SubscriptionPlanChanged, { plan: this.selectedPlan.name });
      const filteredPrices = this.getFilteredPrices(this.selectedPlan?.prices);
      this.handleSubscription(filteredPrices).subscribe(() => { }, () => this.isPlanChangeInProgress = false);
    } else if (this.isUpgrade && !this.isTrialing && !this.isCanceled && this.selectedPlan) {
      this.isLoadingInvoicePreview = true;
      const filteredPrices = this.getFilteredPrices(this.selectedPlan?.prices);
      this.getInvoicePreview(filteredPrices).subscribe(() => { }, () => this.isLoadingInvoicePreview = false);
    }
  }

  getFilteredPrices(prices?: SubscriptionPrice[]): SubscriptionPrice[] {
    if (!prices || !Array.isArray(prices)) {
      return [];
    }

    const licensedPrices = prices.filter(price => price.usageType === SubscriptionType.Licensed);

    // Find the price for the selected interval
    const selectedPrice = licensedPrices.find(price => price.interval === this.selectedInterval);

    if (selectedPrice) {
      return [selectedPrice];
    }

    const fallbackPrice = licensedPrices[0];

    return fallbackPrice ? [fallbackPrice] : [];
  }

  resetToPreviousPlan(): void {
    if (!this.hasCreditCardOnFile) {
      return;
    }
    const initialPlanData = this.isCanceled ? SubscriptionPlanName.None : this.planOptions.find(plan => plan.value === this.initialPlan);
    if (initialPlanData) {
      this.selectedPlanControl.patchValue(initialPlanData);
    }
    // this.selectedInterval = this.initialInterval;
    this.invoicePreview = null;
    this.upgradeToPlanActive = null;
  }

  redirectToBillingPortal() {
    if (this.isExternalBillingProvider) {
      return;
    }

    window.open(this.billingUrl, '_blank');
    if (this.isPastDue) {
      this.hasPaymentFailedTriggered = true;
    }
  }

  refreshPaymentState() {
    return this.user.getUser().pipe(tap(() => this.hasPaymentFailedTriggered = false));
  }

  // returnPath (optional) is threaded so the post-approval confirm redirect can
  // return where the change was initiated (e.g. the native app's billing webview).
  retryProviderBilling(planKey?: string, returnPath?: string): Observable<{ confirmationUrl: string }> {
    const body: any = {};
    if (planKey) { body.planKey = planKey; }
    if (returnPath) { body.returnPath = returnPath; }
    return this.http.post(`${getApiUrl()}billing/provider/retry`,
      body,
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res?.data));
  }

  confirmProviderCharge(chargeId: string): Observable<any> {
    return this.http.post(`${getApiUrl()}billing/provider/confirm-charge`,
      { chargeId },
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res?.data));
  }

  getProviderPlans(): Observable<ProviderPlan[]> {
    return this.http.get(`${getApiUrl()}billing/provider/plans`,
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res?.data));
  }

  // returnPath (optional) is a relative admin path the post-approval confirm
  // redirect will send the merchant back to (e.g. the onboarding flow). When
  // omitted, the backend keeps its default embedded/billing redirect behavior.
  changeProviderPlan(planKey: string, returnPath?: string): Observable<{ confirmationUrl: string }> {
    return this.http.post(`${getApiUrl()}billing/provider/change-plan`,
      returnPath ? { planKey, returnPath } : { planKey },
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res?.data));
  }

  getSubscriptionDetails(subscriptionId: string): Observable<SubscriptionDetails> {
    return this.http.get(`${getApiUrl()}subscriptions/${subscriptionId}`,
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(
        map((res: any) => res?.data),
        tap((details: SubscriptionDetails) => {
          this.subscriptionDetails = details;
          const currentCoupons = details?.currentPlan?.couponsApplied || [];
          const upcomingCoupons = details?.upcomingPlan?.couponsApplied || [];
          this.appliedCoupon = currentCoupons[0] || upcomingCoupons[0] || null;
        })
      );
  }

  pauseSubscription(subscriptionId: string): Observable<void> {
    return this.http.post<void>(`${getApiUrl()}subscriptions/${subscriptionId}/pause`, {},
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(concatMap(() => this.user.getUser()), map(() => void 0));
  }

  unpauseSubscription(subscriptionId: string): Observable<void> {
    return this.http.post<void>(`${getApiUrl()}subscriptions/${subscriptionId}/unpause`, {},
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(concatMap(() => this.user.getUser()), map(() => void 0));
  }

  validateCoupon(subscriptionId: string, couponCode: string, priceId?: string): Observable<any> {
    const data: any = { couponCode };
    if (priceId) {
      data.priceId = priceId;
    }
    return this.http.post(`${getApiUrl()}subscriptions/${subscriptionId}/coupon/validate`,
      data,
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } }
    ).pipe(
      map((res: any) => {
        const data = res?.data;
        this.appliedCoupon = data;
        return data;
      }),
      tap(() => {
        if (this.isUpgrade && !this.isTrialing && !this.isCanceled && this.selectedPlan) {
          const filteredPrices = this.getFilteredPrices(this.selectedPlan?.prices);
          this.getInvoicePreview(filteredPrices).subscribe();
        }
      })
    );
  }

  revalidateCouponForSelectedPlan(): void {
    if (!this.appliedCoupon?.id || !this.subscriptionData?.id || !this.selectedPlan) {
      return;
    }

    // Set flag to prevent loops
    this.isRevalidatingCoupon = true;

    const filteredPrices = this.getFilteredPrices(this.selectedPlan?.prices);
    const licensedPrice = filteredPrices?.find(price => price.usageType === SubscriptionType.Licensed);
    const priceId = licensedPrice?.id;

    this.validateCoupon(this.subscriptionData.id, this.appliedCoupon.code || this.appliedCoupon.id, priceId)
      .subscribe({
        next: (response) => {
          this.appliedCoupon = response;
          if (response?.preview && (this.isTrialing || this.isDowngrade || this.isUpgrade)) {
            this.updatePlanWithPreview(this.selectedPlan, response);
          }
          this.couponChanged.next(this.appliedCoupon.id);
          this.isRevalidatingCoupon = false;
        },
        error: () => {
          this.appliedCoupon = null;
          this.couponChanged.next('');
          this.isRevalidatingCoupon = false;
        }
      });
  }

  updatePlanWithPreview(selectedPlan: any, coupon: any): void {
    if (!coupon?.preview || !selectedPlan) {
      return;
    }

    const priceIdFromResponse = coupon.preview.priceId;
    const matchingPrice = selectedPlan.prices?.find(price => price.id === priceIdFromResponse);
    const intervalString = matchingPrice?.interval || 'month';

    // For upgrades, calculate the full plan prices (not prorated amounts)
    // The preview gives us prorated amounts, but we need full monthly/yearly prices for display
    let originalAmount = coupon.preview.subtotal;
    let discountedAmount = coupon.preview.total;

    if (this.isUpgrade && matchingPrice) {
      // Get the full plan price from the matching price
      const fullPlanPrice = matchingPrice.amount;

      // Calculate the full discounted price using the discount from preview
      const monthlyDiscount = coupon.preview.discount;

      originalAmount = fullPlanPrice;
      discountedAmount = fullPlanPrice - monthlyDiscount;
    }

    // Create display version of the selected plan with coupon preview
    this.selectedPlanDisplay = {
      ...selectedPlan,
      couponPreview: {
        coupon: {
          id: coupon.id,
          name: coupon.name,
          percentOff: coupon.percentOff || null,
          amountOff: coupon.amountOff || null,
          duration: coupon.duration || 'forever',
          durationInMonths: coupon.durationInMonths || null
        },
        prices: [{
          priceId: coupon.preview.priceId,
          interval: intervalString,
          originalAmount: originalAmount,
          discountedAmount: discountedAmount,
          savings: coupon.preview.discount
        }]
      }
    };
  }

  checkUpgradeParam(route: ActivatedRoute, feature: Feature, plan: SubscriptionPlanName): void {
    route.queryParams.pipe(take(1)).subscribe(params => {
      if (params['upgrade'] !== 'true') {
        return;
      }
      // URL-supplied `feature` + `plan` (e.g. from the mobile in-app browser
      // upgrade flow) override the defaults so the modal opens targeting the
      // right tier — Waterfall on Growth vs. Transfer Calls on Scale, etc.
      const urlFeature = this.parseFeatureParam(params['feature']);
      const urlPlan = this.parsePlanParam(params['plan']);
      const resolvedFeature = urlFeature ?? feature;
      const resolvedPlan = urlPlan ?? plan;
      this.router.navigate([], { queryParams: { upgrade: null, feature: null, plan: null }, queryParamsHandling: 'merge', replaceUrl: true });
      this.analytics.trackEvent(AnalyticsEvent.UpgradeToEnablePremiumAction, { feature: resolvedFeature, plan: resolvedPlan });
      // Defer one tick. Deep-tree children's ngOnInit (this method's caller)
      // fires before any ngAfterViewInit, so the NavSidebar subscriber that
      // actually opens the modal isn't attached yet — `Subject.next` here
      // would otherwise emit into the void and the upgrade modal never opens
      // for users landing via an in-app browser deep link from the mobile app.
      setTimeout(() => this.open.next({ feature: resolvedFeature, plan: resolvedPlan }), 0);
    });
  }

  private parseFeatureParam(value: unknown): Feature | undefined {
    if (typeof value !== 'string' || !value) return undefined;
    return Object.values(Feature).includes(value as Feature) ? value as Feature : undefined;
  }

  private parsePlanParam(value: unknown): SubscriptionPlanName | undefined {
    if (typeof value !== 'string' || !value) return undefined;
    return Object.values(SubscriptionPlanName).includes(value as SubscriptionPlanName) ? value as SubscriptionPlanName : undefined;
  }
}

export enum SubscriptionLayout {
  SinglePlanLayout = 'credit-card-entry-layout',
}
